package org.maplibre.compose.sources

import kotlinx.serialization.json.Json
import kotlinx.serialization.json.JsonElement
import kotlinx.serialization.json.JsonObject
import kotlinx.serialization.json.JsonObjectBuilder
import kotlinx.serialization.json.JsonPrimitive
import kotlinx.serialization.json.add
import kotlinx.serialization.json.put
import kotlinx.serialization.json.putJsonArray
import kotlinx.serialization.json.putJsonObject
import org.maplibre.compose.expressions.ast.Expression
import org.maplibre.compose.expressions.ast.ExpressionContext
import org.maplibre.compose.expressions.ast.compile
import org.maplibre.compose.expressions.dsl.const
import org.maplibre.compose.expressions.value.BooleanValue
import org.maplibre.compose.util.toStyleJson
import org.maplibre.spatialk.geojson.toJson

internal fun JsonObjectBuilder.putTileSetOptions(
  options: TileSetOptions,
  includeScheme: Boolean = true,
) {
  put("minzoom", options.minZoom)
  put("maxzoom", options.maxZoom)
  if (includeScheme) {
    put("scheme", options.scheme.value)
  }
  options.boundingBox?.let { box ->
    putJsonArray("bounds") {
      add(box.west)
      add(box.south)
      add(box.east)
      add(box.north)
    }
  }
  options.attributionHtml?.let { put("attribution", it) }
}

internal fun GeoJsonData.toDataJson(): JsonElement =
  when (this) {
    is GeoJsonData.Uri -> JsonPrimitive(uri)
    is GeoJsonData.JsonString -> Json.parseToJsonElement(json)
    is GeoJsonData.Features -> Json.parseToJsonElement(geoJson.toJson())
  }

/** UTF-8 GeoJSON for an inline value, or null when the data is a URL. */
internal fun GeoJsonData.toInlineUtf8(): ByteArray? =
  when (this) {
    is GeoJsonData.Uri -> null
    is GeoJsonData.JsonString -> json.encodeToByteArray()
    is GeoJsonData.Features -> geoJson.toJson().encodeToByteArray()
  }

/**
 * `minzoom` and `synchronousTiling` are deliberately absent: the spec has no place for them here
 * and GL JS rejects the whole source over an unknown key, so each backend that honours one writes
 * it itself.
 */
internal fun JsonObjectBuilder.putGeoJsonOptions(options: GeoJsonOptions) {
  put("maxzoom", options.maxZoom)
  put("buffer", options.buffer)
  put("tolerance", options.tolerance)
  put("lineMetrics", options.lineMetrics)
  put("cluster", options.cluster)
  put("clusterRadius", options.clusterRadius)
  put("clusterMaxZoom", options.clusterMaxZoom)
  put("clusterMinPoints", options.clusterMinPoints)
  if (options.clusterProperties.isEmpty()) return
  putJsonObject("clusterProperties") { putClusterProperties(options.clusterProperties) }
}

/** Each aggregator as the style spec's pair: `[reducer, mapper]`. */
internal fun JsonObjectBuilder.putClusterProperties(
  properties: Map<String, GeoJsonOptions.ClusterPropertyAggregator<*>>
) {
  properties.forEach { (name, aggregator) ->
    putJsonArray(name) {
      add(aggregator.reducer.compile(ExpressionContext.None).toStyleJson())
      add(aggregator.mapper.compile(ExpressionContext.None).toStyleJson())
    }
  }
}

/**
 * A source-feature query's predicate as a style-spec filter, or null to match every feature.
 * MapLibre reads an absent filter as "match everything", and a scalar true is not a filter.
 */
internal fun Expression<BooleanValue>.toFilterJson(): JsonElement? = takeUnless {
  it == const(true)
}
  ?.compile(ExpressionContext.None)
  ?.toStyleJson()

/**
 * Rebuilds a base-style source from what MapLibre reports about it. A style-spec `type` with no
 * public class becomes an internal subclass: GL JS draws video and canvas sources with raster
 * layers, and any other type, or a missing one, has no known layer kind.
 */
internal fun reconstructedSource(id: String, definition: JsonObject): Source =
  when ((definition["type"] as? JsonPrimitive)?.content) {
    "vector" -> VectorTileSource(id, definition)
    "raster" -> RasterTileSource(id, definition)
    "raster-dem" -> RasterDemTileSource(id, definition)
    "geojson" -> GeoJsonSource(id, definition)
    "image" -> ImageSource(id, definition)
    "video",
    "canvas" -> UnmodeledRasterSource(id, definition)
    else -> UnmodeledSource(id, definition)
  }
